/**
 * CopyButton component for copying text to clipboard
 */

import { useState, useCallback } from 'react';
import { Copy, Check } from 'lucide-react';
import { clsx } from 'clsx';
import { useToast } from './Toast';

interface CopyButtonProps {
  text: string;
  label?: string;
  className?: string;
  showIcon?: boolean;
  variant?: 'inline' | 'button';
}

export function CopyButton({
  text,
  label,
  className,
  showIcon = true,
  variant = 'inline',
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const { success } = useToast();

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      success(`Copied: ${text}`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      success(`Copied: ${text}`);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [text, success]);

  if (variant === 'button') {
    return (
      <button
        onClick={handleCopy}
        className={clsx(
          'inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-2 py-1 text-sm font-mono text-gray-700 transition-colors hover:bg-gray-50',
          copied && 'border-green-200 bg-green-50 text-green-700',
          className
        )}
        title={`Copy ${label || text}`}
      >
        {label || text}
        {showIcon && (
          copied ? (
            <Check className="h-3.5 w-3.5 text-green-500" />
          ) : (
            <Copy className="h-3.5 w-3.5 text-gray-400" />
          )
        )}
      </button>
    );
  }

  // Inline variant
  return (
    <button
      onClick={handleCopy}
      className={clsx(
        'group inline-flex items-center gap-1 rounded px-1 -mx-1 font-mono text-gray-600 transition-colors hover:bg-gray-100',
        copied && 'bg-green-50 text-green-700',
        className
      )}
      title={`Copy ${label || text}`}
    >
      {label || text}
      {showIcon && (
        <span className="opacity-0 group-hover:opacity-100 transition-opacity">
          {copied ? (
            <Check className="h-3 w-3 text-green-500" />
          ) : (
            <Copy className="h-3 w-3 text-gray-400" />
          )}
        </span>
      )}
    </button>
  );
}

/**
 * IP address with copy functionality
 */
export function CopyableIP({ ip, className }: { ip: string; className?: string }) {
  return <CopyButton text={ip} className={clsx('text-sm', className)} />;
}

/**
 * Hostname with copy functionality
 */
export function CopyableHostname({
  hostname,
  domain,
  className,
}: {
  hostname: string;
  domain?: string;
  className?: string;
}) {
  const fullHostname = domain ? `${hostname}.${domain}` : hostname;
  return (
    <CopyButton
      text={fullHostname}
      label={hostname}
      className={clsx('font-medium', className)}
    />
  );
}
